import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow
} from "src/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from "src/components/ui/tooltip";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
	useReactTable,
	flexRender,
	getCoreRowModel,
	SortingState,
	getSortedRowModel
} from "@tanstack/react-table";
import { useState } from "react";
import UserChipItem from "src/components/UserSearchItem/UserChipItem";
import getRemainingMonthsInYear from "../../utils/getRemainingMonthsInYear/getRemainingMonthsInYear";
import dayjs from "dayjs";
import EmptyState from "src/components/EmptyState/EmptyState";

type DataTableProps<Data extends object> = {
	data: Data[];
	columns: any;
	onFormSelect?: (id: string, month: string, observationform: number) => void;
	calendarMonth?: Month;
	observationType?: IObservationType;
	calendarYear?: number;
	emptyDataMessage: string;
};

const mapCell = (userData: IUserReport): StoreUser => ({
	uuid: userData.user_uuid,
	id: userData.user_id,
	name: userData.user_name,
	role: userData.user_role,
	store: userData.store_id,
	employee_id: null
});

const UserDataTable = <Data extends object>({
	data,
	columns,
	onFormSelect,
	calendarMonth,
	observationType,
	calendarYear,
	emptyDataMessage
}: DataTableProps<Data>) => {
	const [sorting, setSorting] = useState<SortingState>([]);

	const { getHeaderGroups, getRowModel, getFooterGroups } = useReactTable({
		columns,
		data,
		getCoreRowModel: getCoreRowModel(),
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
		state: {
			sorting
		}
	});

	const renderRows = () => {
		if (getRowModel().rows.length === 0) {
			return (
				<TableRow>
					<TableCell colSpan={getHeaderGroups()[0].headers.length}>
						<EmptyState title={emptyDataMessage} />
					</TableCell>
				</TableRow>
			);
		}

		return getRowModel().rows.map((row: any) => {
			return (
				<TableRow
					key={row.id}
					className="group/row hover:bg-primary-lighter/50"
				>
					{row.getVisibleCells().map((cell: any, index: number) => {
						const rowMonth = cell.getContext().column.id;
						const calendarMonthExists = calendarMonth ? 0.3 : 1;
						const isNotFirstRow = index === 0 ? 1 : calendarMonthExists;

						const isClickable =
							onFormSelect &&
							cell.getValue() &&
							cell.getValue().compliant !== undefined &&
							cell.getValue().complete !== undefined &&
							cell.row.original.observationform !== null;

						const currentDate = dayjs();

						const lastDayOfYear =
							calendarYear && dayjs().year(calendarYear).endOf("year");

						const showBlankRow =
							!currentDate.isAfter(lastDayOfYear) &&
							observationType === "GREAT_CONVERSATION" &&
							getRemainingMonthsInYear(currentDate).includes(rowMonth);

						const opacity = rowMonth === calendarMonth ? 1 : isNotFirstRow;

						return (
							<TableCell
								key={cell.id}
								className={index === 0 ? "text-left h-14" : "text-center h-14"}
								style={{ opacity }}
							>
								<>
									{index === 0 ? (
										<UserChipItem user={mapCell(cell.row.original)} />
									) : isClickable ? (
										<TooltipProvider>
											<Tooltip>
												<TooltipTrigger asChild>
													<button
														className="min-w-[36px] h-[36px] rounded-lg hover:bg-primary-lighter hover:shadow-sm transition-all duration-150"
														onClick={() =>
															onFormSelect(
																row.original.user_id,
																cell.row.original[cell.column.id].month,
																cell.row.original.observationform
															)
														}
													>
														{flexRender(
															cell.column.columnDef.cell,
															cell.getContext()
														)}
													</button>
												</TooltipTrigger>
												<TooltipContent>View form</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									) : (
										<>
											{showBlankRow ? null : (
												<>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext()
													)}
												</>
											)}
										</>
									)}
								</>
							</TableCell>
						);
					})}
				</TableRow>
			);
		});
	};

	const renderColumns = () => {
		return getHeaderGroups().map((headerGroup) => (
			<TableRow
				key={headerGroup.id}
				className="bg-zinc-50 border-b-2 border-border"
			>
				{headerGroup.headers.map((header, index) => {
					return (
						<TableHead
							key={header.id}
							onClick={header.column.getToggleSortingHandler()}
							className={`capitalize cursor-pointer text-xs font-semibold uppercase tracking-wider text-zinc-600 ${index === 0 ? "text-left" : "text-center"}`}
						>
							<span className="inline-flex items-center gap-1">
								{flexRender(
									header.column.columnDef.header,
									header.getContext()
								)}

								{header.column.getIsSorted() ? (
									header.column.getIsSorted() === "desc" ? (
										<ChevronDown
											className="w-3 h-3"
											aria-label="sorted descending"
										/>
									) : (
										<ChevronUp
											className="w-3 h-3"
											aria-label="sorted ascending"
										/>
									)
								) : null}
							</span>
						</TableHead>
					);
				})}
			</TableRow>
		));
	};

	return (
		<div className="overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
			<Table>
				<TableHeader>{renderColumns()}</TableHeader>

				<TableBody>{renderRows()}</TableBody>

				<TableFooter>
					{getFooterGroups().map((footerGroup) => (
						<TableRow key={footerGroup.id}>
							{footerGroup.headers.map((header) => (
								<TableHead key={header.id} className="font-bold normal-case">
									{!header.isPlaceholder &&
										flexRender(
											header.column.columnDef.footer,
											header.getContext()
										)}
								</TableHead>
							))}
						</TableRow>
					))}
				</TableFooter>
			</Table>
		</div>
	);
};

export default UserDataTable;
